import { AlertTriangle, Database, FileWarning, ShieldCheck, Timer } from 'lucide-react'
import {
  DateValue, EmptyState, ErrorState, PageHeader, StatCard, StatusBadge,
} from '@/components/common'
import { Card } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import { useBreadcrumb } from '@/hooks/useBreadcrumb'
import { useOrphanFiles, useSystemHealth } from '../api'

/**
 * What the running system can prove about itself.
 *
 * <p>Everything on this page is measured when it is asked: the schema version comes from
 * Flyway's own history, the policy counts from {@code pg_class}, the role checks re-run what
 * {@code verify-roles.sql} asserts, and the job list from the live scheduler. Nothing is
 * declared in code and echoed back — a health screen that reports its own configuration is
 * how an outage stays green.
 */
export function SystemHealthPage() {
  useBreadcrumb()
  const health = useSystemHealth()
  const orphans = useOrphanFiles()

  return (
    <div className="space-y-4">
      <PageHeader
        title="System health"
        subtitle="Measured now, from the database and the scheduler."
      />

      {health.isPending && <Skeleton className="h-32" />}
      {health.isError && <ErrorState error={health.error} onRetry={() => void health.refetch()} />}

      {health.isSuccess && (
        <>
          {/* First on the page, and red when it matters: an installation holding the demo
              dataset is holding an administrator whose password is in the source repository,
              and that is the single most useful thing this screen can tell somebody. */}
          <Card
            role={health.data.installation.demoSeeded ? 'alert' : undefined}
            className={`p-4 text-sm ${health.data.installation.demoSeeded
              ? 'border-amber-300 bg-amber-50 text-amber-900'
              : ''}`}
          >
            <p className="flex items-start gap-2 font-medium">
              {health.data.installation.demoSeeded
                ? <AlertTriangle className="mt-0.5 size-4 shrink-0" aria-hidden="true" />
                : <ShieldCheck className="mt-0.5 size-4 shrink-0" aria-hidden="true" />}
              {health.data.installation.demoSeeded
                ? 'Demo installation'
                : 'Customer installation'}
            </p>
            <p className="mt-1">{health.data.installation.note}</p>
            {health.data.installation.bootstrappedBy && (
              <p className="mt-1 text-xs">
                First administrator: {health.data.installation.bootstrappedBy}
                {health.data.installation.bootstrappedAt && (
                  <> · <DateValue value={health.data.installation.bootstrappedAt} /></>
                )}
              </p>
            )}
          </Card>

          {/* Work that has stopped moving without failing. None of these produces an error
              or a log line, which is why they are on a screen rather than left to logs. */}
          <Card className="p-4">
            <h2 className="font-medium">Is anything stuck?</h2>
            <div className="mt-3 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
              {([
                ['Payroll runs awaiting approval', health.data.operational.payrollRunsAwaitingApproval],
                ['Documents already expired', health.data.operational.documentsExpired],
                ['Documents expiring in 30 days', health.data.operational.documentsExpiringSoon],
                ['Webhooks queued', health.data.operational.webhookBacklog],
                ['Webhooks that gave up', health.data.operational.webhookFailed],
                ['Events waiting to dispatch', health.data.operational.outboxBacklog],
                ['Events that will never dispatch', health.data.operational.outboxDead],
                ['Triggers in error', health.data.operational.triggersInError],
              ] as [string, number][]).map(([label, value]) => (
                <div key={label} className="rounded-md border border-[var(--color-border)] p-3">
                  <p className={`text-2xl font-semibold ${value > 0 ? 'text-amber-700' : ''}`}>
                    {value < 0 ? '—' : value}
                  </p>
                  <p className="text-xs text-slate-600">{label}</p>
                </div>
              ))}
            </div>
            <p className="mt-3 text-xs text-slate-500">{health.data.operational.note}</p>
          </Card>

          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            <StatCard
              label="Schema version"
              value={health.data.schemaVersion}
              icon={Database}
              caption={health.data.schemaChecksum ?? 'No checksum recorded'}
            />
            <StatCard
              label="Tables carrying a tenant"
              value={String(health.data.tenantTables)}
              icon={ShieldCheck}
              caption={`${health.data.protectedTables} with row-level security forced`}
            />
            <StatCard label="Policies" value={String(health.data.policies)} icon={ShieldCheck} />
            <StatCard
              label="Files nothing points at"
              value={String(health.data.orphanFiles)}
              icon={FileWarning}
              caption={`${(health.data.orphanBytes / 1_048_576).toFixed(1)} MB`}
            />
          </div>

          <Card className="p-3">
            <p className="text-sm font-medium text-[var(--color-heading)]">Database roles</p>
            <p className="mt-1 text-xs text-[var(--color-muted)]">
              The same assertions <span className="font-mono">verify-roles.sql</span> makes at
              deploy time, re-run against the database this application is connected to.
            </p>
            <ul className="mt-3 space-y-2">
              {health.data.roleChecks.map((check) => (
                <li key={check.name} className="flex flex-wrap items-center gap-2 text-sm">
                  <StatusBadge
                    status={check.status}
                    tone={check.status === 'PASS' ? 'success' : 'danger'}
                    label={check.status === 'PASS' ? 'Pass' : 'Fail'}
                  />
                  <span className="font-medium text-[var(--color-heading)]">{check.name}</span>
                  <span className="text-[var(--color-muted)]">{check.detail}</span>
                </li>
              ))}
            </ul>
          </Card>

          <Card className="p-3">
            <p className="flex items-center gap-2 text-sm font-medium text-[var(--color-heading)]">
              <Timer className="size-4" aria-hidden="true" />
              Scheduled jobs
            </p>
            {health.data.jobs.length === 0 ? (
              <p className="mt-2 text-sm text-[var(--color-body)]">
                The scheduler is running and holds no job. Nothing runs on a timer.
              </p>
            ) : (
              <div className="mt-2 overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="text-[var(--color-muted)]">
                    <tr>
                      <th className="py-1 text-start font-medium">Job</th>
                      <th className="py-1 text-start font-medium">Schedule</th>
                      <th className="py-1 text-start font-medium">Next</th>
                      <th className="py-1 text-start font-medium">State</th>
                    </tr>
                  </thead>
                  <tbody>
                    {health.data.jobs.map((job) => (
                      <tr key={`${job.group}.${job.name}`} className="border-t border-[var(--color-border)]">
                        <td className="py-2">{job.name}</td>
                        <td className="py-2 font-mono text-xs">{job.cron ?? '—'}</td>
                        <td className="py-2">
                          {job.nextFireTime
                            ? <DateValue value={job.nextFireTime} withTime />
                            : <span className="text-[var(--color-muted)]">Not scheduled</span>}
                        </td>
                        <td className="py-2">{job.state}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </Card>

          <Card className="p-3">
            <p className="text-sm font-medium text-[var(--color-heading)]">Backups</p>
            <p className="mt-1 text-sm text-[var(--color-body)]">{health.data.backupNote}</p>
          </Card>

          <Card className="p-3">
            <p className="text-sm font-medium text-[var(--color-heading)]">Impersonation</p>
            <p className="mt-1 text-sm text-[var(--color-body)]">{health.data.impersonationNote}</p>
          </Card>
        </>
      )}

      <Card>
        <p className="border-b border-[var(--color-border)] p-3 text-sm font-medium text-[var(--color-heading)]">
          Orphaned files
        </p>
        <p className="p-3 text-xs text-[var(--color-muted)]">
          Uploaded and then never attached to anything. They are listed, never swept: deleting
          a file because no row points at it today is how an attachment disappears the moment a
          module is written that would have claimed it.
        </p>
        {orphans.isPending && <Skeleton className="m-4 h-16" />}
        {orphans.isError && (
          <ErrorState error={orphans.error} onRetry={() => void orphans.refetch()} />
        )}
        {orphans.isSuccess && orphans.data.length === 0 && (
          <EmptyState title="Nothing orphaned" description="Every stored file is claimed by a row." />
        )}
        {orphans.isSuccess && orphans.data.length > 0 && (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="border-b border-[var(--color-border)] text-[var(--color-muted)]">
                <tr>
                  <th className="p-3 text-start font-medium">File</th>
                  <th className="p-3 text-start font-medium">Meant for</th>
                  <th className="p-3 text-start font-medium">Size</th>
                  <th className="p-3 text-start font-medium">Uploaded</th>
                </tr>
              </thead>
              <tbody>
                {orphans.data.map((file) => (
                  <tr key={file.id} className="border-b border-[var(--color-border)]">
                    <td className="p-3">{file.filename}</td>
                    <td className="p-3">{file.ownerType ?? 'Not stated'}</td>
                    <td className="p-3">{(file.sizeBytes / 1024).toFixed(0)} KB</td>
                    <td className="p-3"><DateValue value={file.createdAt} withTime /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  )
}
